import { createApi } from '@reduxjs/toolkit/query/react';

import {
  GrowthPlanMeetingRequest,
  Guest,
  ScaleupGrowthPlan,
  SessionOutcomeHolder,
  SessionWorkHolder,
} from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';
import { list } from 'aws-amplify/storage';

export const growthPlanApi = createApi({
  reducerPath: 'growthPlanApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.growthPlan],
  endpoints: (builder) => ({
    getGrowthPlan: builder.query<ScaleupGrowthPlan, void>({
      query: () => '/growth_plan',
      providesTags: [TagTypes.growthPlan],
    }),
    // PUT /growth_plan/session/{sessionNumber}/guests
    updateSessionsGuests: builder.mutation<
      void,
      { sessionNumber: number | string; guests: Guest[] }
    >({
      query: ({ sessionNumber, guests }) => ({
        url: `/growth_plan/session/${sessionNumber}/guests`,
        method: 'PUT',
        body: guests,
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    // POST /growth_plan/session/{sessionNumber}/meeting
    sendGrowthPlanMeetingRequest: builder.mutation<
      void,
      { meeting: GrowthPlanMeetingRequest; sessionNumber: string | number }
    >({
      query: ({ meeting, sessionNumber }) => ({
        url: `/growth_plan/session/${sessionNumber}/meeting`,
        method: 'POST',
        body: meeting,
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    // PUT /growth_plan/session/{sessionNumber}/meeting
    rescheduleGrowthPlanMeeting: builder.mutation<
      void,
      { meeting: GrowthPlanMeetingRequest; sessionNumber: string | number }
    >({
      query: ({ meeting, sessionNumber }) => ({
        url: `/growth_plan/session/${sessionNumber}/meeting`,
        method: 'PUT',
        body: meeting,
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    getSessionAvailableTimes: builder.query<
      Record<string, string[]>,
      { timezone: string; month: number; year: number }
    >({
      query: ({ timezone, month, year }) =>
        `/growth_plan/available_times?tz=${timezone}&month=${month}&year=${year}`,
      providesTags: (res, error, { timezone, month, year }) => [
        {
          type: TagTypes.growthPlanSessionTimes,
          id: `${timezone}${month}${year}`,
        },
      ],
    }),
    // PUT /growth_plan/session/{sessionNumber}/outcomes
    updateSessionOutcomes: builder.mutation<
      void,
      { sessionNumber: number | string; outcomes: SessionOutcomeHolder }
    >({
      query: ({ sessionNumber, outcomes }) => ({
        url: `/growth_plan/session/${sessionNumber}/outcomes`,
        method: 'PUT',
        body: outcomes,
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    // PUT /growth_plan/session/{sessionNumber}/prework
    updatePrework: builder.mutation<
      void,
      { sessionNumber: number | string; preWork: SessionWorkHolder }
    >({
      query: ({ sessionNumber, preWork }) => ({
        url: `/growth_plan/session/${sessionNumber}/prework`,
        method: 'PUT',
        body: preWork,
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    // PUT /growth_plan/session_count
    updateSessionCount: builder.mutation<
      void,
      { sessionCount: number }
    >({
      query: ({ sessionCount }) => ({
        url: `/growth_plan/session_count`,
        method: 'PUT',
        body: { sessionCount },
      }),
      invalidatesTags: [TagTypes.growthPlan],
    }),
    //GET /growth_plan/docs
    listGrowthPlanDocs: builder.query<string[], void>({
      query: () => '/growth_plan/docs',
    }),
    // POST /growth_plan/docs
    //TODO: change the endpoint to accept file and content type as json object in body, and return presigned url for upload
    uploadGrowthPlanDoc: builder.mutation<{ presignedUrl: string },{ fileName: string; contentType: string }>({
      query: ({ fileName, contentType }) => ({
        url: `/growth_plan/docs?content_type=${contentType}&file_name=${fileName}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetGrowthPlanQuery,
  useUpdateSessionsGuestsMutation,
  useSendGrowthPlanMeetingRequestMutation,
  useRescheduleGrowthPlanMeetingMutation,
  useLazyGetSessionAvailableTimesQuery,
  useUpdateSessionOutcomesMutation,
  useUpdatePreworkMutation,
  useUpdateSessionCountMutation,
  useListGrowthPlanDocsQuery,
  useUploadGrowthPlanDocMutation,
} = growthPlanApi;
